import React, { useEffect, useState, forwardRef } from "react";
import Modal from "react-bootstrap/Modal";
import { Row, Col } from "react-bootstrap";
import { ComboboxField } from "../../../../Components/formElements/ComboboxField";
import { InputField } from "../../../../Components/formElements/InputField";
import { TextAreaField } from "../../../../Components/formElements/TextAreaField";
import DataTable from "react-data-table-component";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { getMRUserList } from "../../../../redux/feature/customer_app/customerSlice";
import ItemService from "../../../../services/customer_appCommonService";
import { serviceAssignentCategoryOptionForExtension } from "./constants";

const formatIST = (isoString) => {
  if (!isoString) return { date: "--", time: "" };
  const d = new Date(isoString);
  return {
    date: d.toLocaleDateString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }),
    time: d.toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }),
  };
};

/**
 * ERFExtensionAssignSEModal
 *
 * Props:
 *  - show / onHide / erfData / onSuccess  — same as before
 *  - viewOnly (bool) — when true the modal shows only history (no assign form)
 */
const ERFExtensionAssignSEModal = forwardRef(
  ({ show, onHide, erfData, onSuccess, viewOnly = false }, ref) => {
    const dispatch = useDispatch();
    const userData = useSelector((state) => state?.user);
    const mrUserData = useSelector((state) => state?.customer?.mrUserList);

    const [loading, setLoading] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState("");
    const [selectedSEData, setSelectedSEData] = useState({});
    const [shouldCollectMoney, setShouldCollectMoney] = useState(true);
    const [formInputs, setFormInputs] = useState({});
    const [getDropdownData, setDropdownData] = useState("");
    // eslint-disable-next-line
    const [validation, setValidation] = useState({});

    // ── Load SE list on open (only when not viewOnly) ────────────────────────
    useEffect(() => {
      if (show && !viewOnly) {
        dispatch(
          getMRUserList({
            page: 1,
            per_page: 999,
            queryName: "get_ServiceProvider_list",
            timeZone: "Asia/Calcutta",
          }),
        );
      }
    }, [show, dispatch, viewOnly]);

    // ── Reset on close ───────────────────────────────────────────────────────
    useEffect(() => {
      if (!show) {
        setSelectedSupplier("");
        setSelectedSEData({});
        setFormInputs({});
        setValidation({});
        setShouldCollectMoney(true);
      }
    }, [show]);

    // ── Dropdown handler ─────────────────────────────────────────────────────
    useEffect(() => {
      if (!getDropdownData) return;
      if (getDropdownData.textField === "serviceAssignCategory") {
        setFormInputs((prev) => ({
          ...prev,
          [getDropdownData.textField]: getDropdownData.name,
          [getDropdownData.textCode]: getDropdownData.code,
          [getDropdownData.textDesc]: getDropdownData.description,
        }));
      }
      setValidation((prev) => ({
        ...prev,
        [getDropdownData.textField]: "",
      }));
    }, [getDropdownData]);

    const handleSEChange = (e) => {
      const value = e.target.value;
      setSelectedSupplier(value);
      const selected = mrUserData?.data?.find((item) => item.userId === value);
      if (selected) setSelectedSEData(selected);
    };

    // ── Guard: can we assign? ─────────────────────────────────────────────────
    // Block if the current SE's job is still "Pending" (they haven't responded yet).
    // After SE marks "Not Done" → jobDoneStatus = "Not Done" → unblocked.
    // After SE marks "Done"     → jobDoneStatus = "Done"     → unblocked (but status is REPAYCOLLECTED anyway).
    const currentJobPending =
      erfData?.jobDoneStatus === "Pending" && !!erfData?.serviceProviderUserId;

    // Assignment is only allowed when status is PAYPENDING.
    // After "Not Done" we revert erfStatusCode → PAYPENDING, so this gate re-opens.
    const statusAllowsAssignment = erfData?.erfStatusCode === "PAYPENDING";

    const canAssign = statusAllowsAssignment && !currentJobPending && !viewOnly;

    // ── Assign handler ───────────────────────────────────────────────────────
    const handleAssign = async () => {
      if (!canAssign) return;

      if (!selectedSEData?.userId) {
        toast.error("Please select a Service Executive.");
        return;
      }
      if (!formInputs.serviceAssignCategory) {
        toast.error("Please select an Assignment Category.");
        return;
      }
      if (!formInputs.serviceAssignCategoryDescription) {
        toast.error("Please enter a Service Description.");
        return;
      }

      setLoading(true);
      try {
        const assignTimestamp = new Date().toISOString();

        const newAssignmentEntry = {
          serviceProviderCode: selectedSEData.userId,
          serviceProviderUserId: selectedSEData.userId,
          serviceProviderName:
            selectedSEData.firstName || selectedSEData.name || "",
          serviceAssignCategory: formInputs.serviceAssignCategory,
          serviceAssignCategoryCode: formInputs.serviceAssignCategoryCode,
          serviceAssignCategoryDescription:
            formInputs.serviceAssignCategoryDescription,
          assignDateAndTime: assignTimestamp,
          assignedBy: userData?.data?.userId,
          shouldCollectMoney,
          jobStatus: "Pending",
          jobStatusRemarks: "",
        };

        const payload = {
          documentNumber: erfData.documentNumber,
          equipmentRentNr: erfData.documentNumber,
          equipmentNr: erfData.equipmentNr,

          serviceProviderCode: selectedSEData.userId,
          serviceProviderUserId: selectedSEData.userId,
          serviceProviderName:
            selectedSEData.firstName || selectedSEData.name || "",
          serviceAssignCategory: formInputs.serviceAssignCategory,
          serviceAssignCategoryCode: formInputs.serviceAssignCategoryCode,
          serviceAssignCategoryDescription:
            formInputs.serviceAssignCategoryDescription,
          assignDateAndTime: assignTimestamp,
          assignedServiceExecutives: [
            ...(erfData.assignedServiceExecutives || []),
            newAssignmentEntry,
          ],
          shouldCollectMoney,
          jobDoneStatus: "Pending",
          jobDoneRemarks: "",

          // Status change: PAYPENDING → REUSE
          erfStatus: "RE Under Service Executive",
          erfStatusCode: "REUSE",
        };

        await ItemService.updateExtendEquipmentRent(payload);
        toast.success("Service Executive assigned to Extension successfully");
        onHide();
        onSuccess?.();
      } catch (err) {
        toast.error("Failed to assign Service Executive");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    // ── History columns ───────────────────────────────────────────────────────
    const historyColumns = [
      {
        name: "Assigned Date",
        width: "150px",
        cell: (row) => {
          const { date, time } = formatIST(row.assignDateAndTime);
          return (
            <div>
              <p className="m-0 fw-medium">{date}</p>
              <p className="m-0 text-muted" style={{ fontSize: "11px" }}>
                {time}
              </p>
            </div>
          );
        },
      },
      {
        name: "Service Executive",
        width: "180px",
        cell: (row) => (
          <div>
            <p className="m-0 fw-bold">{row.serviceProviderName || "--"}</p>
            <p className="m-0 text-muted" style={{ fontSize: "11px" }}>
              {row.serviceProviderCode}
            </p>
          </div>
        ),
      },
      {
        name: "Category",
        width: "150px",
        selector: (row) => row.serviceAssignCategory || "--",
      },
      {
        name: "Description",
        width: "250px",
        selector: (row) => row.serviceAssignCategoryDescription || "--",
      },
      {
        name: "Assigned By",
        width: "140px",
        selector: (row) => row.assignedBy || "--",
      },
      {
        name: "Collect Money",
        width: "120px",
        cell: (row) => (
          <span
            className={
              row.shouldCollectMoney ? "text-success fw-bold" : "text-muted"
            }
          >
            {row.shouldCollectMoney ? "Yes" : "No"}
          </span>
        ),
      },
      {
        name: "Job Status",
        width: "130px",
        cell: (row) => {
          const s = row.jobStatus || "Pending";
          const color =
            s === "Done" ? "#28a745" : s === "Not Done" ? "#dc3545" : "#ffc107";
          return (
            <span
              className="badge"
              style={{
                backgroundColor: color,
                color: s === "Pending" ? "black" : "white",
                padding: "6px 10px",
                borderRadius: "10px",
              }}
            >
              {s}
            </span>
          );
        },
      },
      {
        name: "Job Remarks",
        width: "220px",
        cell: (row) => (
          <div>
            <p className="m-0 small">{row.jobStatusRemarks || "--"}</p>
            {row.jobStatusUpdatedBy && (
              <p className="m-0 text-muted" style={{ fontSize: "11px" }}>
                by {row.jobStatusUpdatedBy}
              </p>
            )}
          </div>
        ),
      },
    ];

    return (
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        centered
        backdrop="static"
        ref={ref}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {viewOnly ? "View SE Assignment — " : "Assign Service Executive — "}
            {erfData?.documentNumber}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* ── Guard banners (shown only when NOT view-only) ─────────────── */}
          {!viewOnly && currentJobPending && (
            <div className="alert alert-warning">
              <i className="bi bi-exclamation-triangle me-2" />
              Current SE (<strong>{erfData?.serviceProviderName}</strong>) has a{" "}
              <strong>Pending</strong> job. The SE must mark it Done or Not Done
              before re-assign.
            </div>
          )}
          {/* ── Assignment form — hidden in viewOnly mode ─────────────────── */}
          {!viewOnly && (
            <Row className="mb-3">
              <Col xs={12} md={4} className="commTopButtonRightLeftPadding">
                <ComboboxField
                  label="Assignment Category"
                  name="serviceAssignCategory"
                  code="serviceAssignCategoryCode"
                  description="serviceAssignCategoryDesc"
                  data={serviceAssignentCategoryOptionForExtension}
                  setValue={formInputs.serviceAssignCategory}
                  getvalue={setDropdownData}
                  required
                  disabled={!canAssign}
                />
              </Col>
              <Col xs={12} md={4} className="commTopButtonRightLeftPadding">
                <label className="form-label">Service Executive *</label>
                <select
                  className="form-control inputBox"
                  value={selectedSupplier}
                  onChange={handleSEChange}
                  disabled={!canAssign}
                >
                  <option value="">Select Service Executive</option>
                  {mrUserData?.data?.map((item) => (
                    <option key={item.userId} value={item.userId}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </Col>
              <Col xs={12} md={4} className="commTopButtonRightLeftPadding">
                <InputField
                  label="SE Code"
                  className="inputBox"
                  value={selectedSEData?.userId || ""}
                  disabled
                />
              </Col>
              <Col
                xs={12}
                md={8}
                className="commTopButtonRightLeftPadding mt-2"
              >
                <TextAreaField
                  label="Service Description *"
                  className="inputBox"
                  name="serviceAssignCategoryDescription"
                  value={formInputs.serviceAssignCategoryDescription || ""}
                  onChange={(e) =>
                    setFormInputs((p) => ({
                      ...p,
                      serviceAssignCategoryDescription: e.target.value,
                    }))
                  }
                  disabled={!canAssign}
                />
              </Col>
              <Col xs={12} className="commTopButtonRightLeftPadding mt-2">
                <div className="form-check d-flex align-items-center gap-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="extShouldCollectMoney"
                    style={{ width: "20px", height: "20px", cursor: "pointer" }}
                    checked={shouldCollectMoney}
                    onChange={(e) => setShouldCollectMoney(e.target.checked)}
                    disabled={!canAssign}
                  />
                  <label
                    className="form-check-label fw-bold mt-1"
                    htmlFor="extShouldCollectMoney"
                  >
                    SE needs to collect payment
                  </label>
                </div>
              </Col>
            </Row>
          )}

          {/* ── Action buttons ────────────────────────────────────────────── */}
          {!viewOnly && (
            <Row className="mb-4">
              <Col xs={12} className="d-flex gap-2">
                <button className="btnTable btn-danger" onClick={onHide}>
                  Cancel
                </button>
                <button
                  className="btnTable"
                  onClick={handleAssign}
                  disabled={loading || !canAssign}
                >
                  {loading ? "Assigning..." : "Assign Service Executive"}
                </button>
              </Col>
            </Row>
          )}

          {/* ── Assignment history — always shown ─────────────────────────── */}
          <div className="mt-3">
            <h6 className="text-primary fw-bold ps-1 mb-2">
              <i className="bi bi-clock-history me-2" />
              Service Executive Assignment History
            </h6>
            {erfData?.assignedServiceExecutives?.length > 0 ? (
              <DataTable
                columns={historyColumns}
                data={[...erfData.assignedServiceExecutives].reverse()}
                pagination
                noDataComponent={
                  <div className="p-3 text-center text-muted">
                    No assignment history found
                  </div>
                }
              />
            ) : (
              <div className="p-3 text-center text-muted border rounded">
                No SE has been assigned yet.
              </div>
            )}
          </div>
        </Modal.Body>

        {viewOnly && (
          <Modal.Footer>
            <Col xs={12} md={4} className="commTopButtonRightLeftPadding">
              <button className="btnTable btn-secondary" onClick={onHide}>
                Close
              </button>
            </Col>
          </Modal.Footer>
        )}
      </Modal>
    );
  },
);

export default ERFExtensionAssignSEModal;
